'use client';

import { getCTA } from '@/utils/getCTA';
import { getChildHeadingVariant } from '@/utils/headingLevel';
import { getColorScheme } from '@/utils/getColorScheme';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import IconValue from './atoms/IconValue';
import { MarkDown } from './atoms/MarkDown';

const normalizeAnimation = (raw) => {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    const map = {
        '': 'fade',
        none: 'none',
        fade: 'fade',
        'fade in': 'fade',
        slide: 'slide',
        'slide up': 'slide',
        zoom: 'zoom',
        'zoom in': 'zoom',
        stagger: 'stagger',
        cascade: 'stagger',
    };
    return map[v] ?? 'fade';
};

const mapGroup = (raw) => {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    if (['ctas', 'cta', 'buttons', 'ctas-only', 'cta-only'].includes(v))
        return 'ctas';
    if (['features', 'feature', 'features-only', 'feature-only'].includes(v))
        return 'features';
    return 'content';
};

const getElementVariants = (preset, duration) => {
    const d = Math.max(0.2, Number(duration) || 0.6);
    if (preset === 'slide') {
        return {
            offscreen: { opacity: 0, y: 16 },
            onscreen: {
                opacity: 1,
                y: 0,
                transition: { type: 'tween', duration: d },
            },
        };
    }
    if (preset === 'zoom') {
        return {
            offscreen: { opacity: 0, scale: 0.96 },
            onscreen: {
                opacity: 1,
                scale: 1,
                transition: { type: 'tween', duration: d },
            },
        };
    }
    return {
        offscreen: { opacity: 0 },
        onscreen: { opacity: 1, transition: { type: 'tween', duration: d } },
    };
};

const getStaggerContainer = (duration) => {
    const d = Math.max(0.2, Number(duration) || 0.6);
    return {
        hidden: { opacity: 1 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: Math.min(0.25, d * 0.35),
                delayChildren: Math.min(0.3, d * 0.4),
            },
        },
    };
};

function MotionMaybe({ enable, k, motionProps, className, children }) {
    if (enable) {
        return (
            <motion.div key={`${k}-on`} className={className} {...motionProps}>
                {children}
            </motion.div>
        );
    }
    return (
        <div
            key={`${k}-off`}
            className={className}
            style={{ opacity: '', transform: '' }}
        >
            {children}
        </div>
    );
}

function Feature(props) {
    const { Icon, Item, iconColor } = props;

    return (
        <div className="relative flex pl-5">
            <div className="inline text-base">
                <IconValue
                    value={Icon}
                    className={`absolute left-0 mt-1 text-base ${iconColor}`}
                />
                <span className="font-semibold">{Item} </span>
            </div>
        </div>
    );
}

export default function Pricing(props) {
    const {
        Tagline,
        Anchor,
        Header,
        Body,
        Packages,
        ColorScheme = 'light',
        VerticalSpacing = 'medium',
        Animation = 'fade',
        Duration = 0.6,
        AnimationGroup = 'content',
        EditorPreview = false,
    } = props;
    const packageHeadingVariant = getChildHeadingVariant(Header);

    let numColumns;

    if (Packages.length === 1) {
        numColumns = 'grid-cols-1';
    } else if (Packages.length === 2) {
        numColumns = 'grid-cols-1 md:grid-cols-2';
    } else if (Packages.length === 3) {
        numColumns = 'grid-cols-1 lg:grid-cols-3';
    } else if (Packages.length === 4) {
        numColumns = 'grid-cols-1 md:grid-cols-2';
    } else if (Packages.length === 5) {
        numColumns = 'grid-cols-1 md:grid-cols-2';
    } else if (Packages.length === 6) {
        numColumns = 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
    } else if (Packages.length === 7) {
        numColumns = 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
    } else if (Packages.length === 8) {
        numColumns = 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-4';
    } else {
        // For 9 or more images
        numColumns = 'grid-cols-1 lg:grid-cols-3';
    }
    const {
        backgroundColor,
        textColor,
        headerColor,
        iconColor,
        boxColor,
        taglineColor,
        boxTextColor,
        boxBorder,
        thirdButton,
        secondaryButton,
        boxThirdButton,
        boxSecondaryButton,
        backgroundStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);

    const buttonVariant = boxColor ? boxSecondaryButton : secondaryButton;
    const buttonVariantOutline = boxColor ? boxThirdButton : thirdButton;

    const vSpace = getVerticalSpacing(VerticalSpacing);

    /* -------------------------- Animation -------------------------- */
    const preset = normalizeAnimation(Animation);
    const requestedGroup = mapGroup(AnimationGroup);
    const [prefersReduced, setPrefersReduced] = useState(false);
    useEffect(() => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
            const update = (e) => setPrefersReduced(e.matches);
            setPrefersReduced(mq.matches);
            mq.addEventListener?.('change', update);
            return () => mq.removeEventListener?.('change', update);
        }
    }, []);

    const isStagger = preset === 'stagger';
    const enableAnim = !prefersReduced && preset !== 'none';
    const viewport = { once: !EditorPreview, amount: 0.5 };
    const hasFeatures = Array.isArray(Packages) && Packages.length > 0;
    const hasAnyCTA = Boolean(
        props.CTA || props.SecondaryButtonText || props.OutlineButtonText
    );
    const group =
        requestedGroup === 'ctas' && !hasAnyCTA
            ? 'content'
            : requestedGroup === 'features' && !hasFeatures
            ? 'content'
            : requestedGroup;
    const animateText = group === 'content';
    const animateFeatures = group === 'content' || group === 'features';
    const animateCTAs = group === 'ctas';

    const baseKey = `pricing-${group}-${preset}-${Duration}-${
        EditorPreview ? 'preview' : 'prod'
    }`;

    const containerMotionProps = useMemo(() => {
        if (!enableAnim || !isStagger) return {};
        const variants = getStaggerContainer(Duration);
        return EditorPreview
            ? { variants, initial: 'hidden', animate: 'show' }
            : { variants, initial: 'hidden', whileInView: 'show', viewport };
    }, [enableAnim, isStagger, Duration, EditorPreview]);

    const childMotionProps = useMemo(() => {
        if (!enableAnim) return {};

        if (isStagger) {
            const childVariants = {
                hidden: { opacity: 0, y: 16 },
                show: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: Math.max(0.4, Duration - 0.2) },
                },
            };
            return { variants: childVariants };
        }

        const v = getElementVariants(preset, Duration);
        const hasOffscreen = v && 'offscreen' in v;
        const initialState = hasOffscreen ? 'offscreen' : 'hidden';
        const targetState = hasOffscreen ? 'onscreen' : 'show';
        return EditorPreview
            ? { variants: v, initial: initialState, animate: targetState }
            : {
                  variants: v,
                  initial: initialState,
                  whileInView: targetState,
                  viewport,
              };
    }, [enableAnim, preset, isStagger, Duration, EditorPreview]);

    const { hasAny: hasMainCTAs, element: mainCTAs } = getCTA(props, {
        secondaryVariant: secondaryButton,
        outlineVariant: thirdButton,
        buttonAlign: 'justify-center',
        wrapSpacing: 'mt-10 lg:mt-16',
        size: 'md',
        enableAnim: animateCTAs && enableAnim,
        motionProps: childMotionProps,
    });

    return (
        <div
            id={Anchor}
            className={`editable-component relative ${backgroundColor} ${vSpace}`}
            style={backgroundStyle}
            data-json="pricing"
        >
            {Header && (
                <div className="relative z-40 mx-auto px-6 pb-16 sm:max-w-xl md:max-w-full md:px-24 lg:max-w-screen-xl lg:px-8">
                    <div className="mb-6 max-w-xl text-center md:mx-auto md:mb-10 lg:max-w-2xl">
                        <MotionMaybe
                            enable={animateText && enableAnim}
                            k={`${baseKey}-tagline`}
                            motionProps={childMotionProps}
                        >
                            <MarkDown
                                className={`font-display text-base uppercase leading-7 tracking-wide ${taglineColor}`}
                            >
                                {Tagline}
                            </MarkDown>
                        </MotionMaybe>
                        <MotionMaybe
                            enable={animateText && enableAnim}
                            k={`${baseKey}-header`}
                            motionProps={childMotionProps}
                        >
                            <MarkDown
                                variant="h2"
                                className={`mt-2 font-display text-3xl tracking-tight sm:text-4xl sm:leading-tight ${headerColor}`}
                            >
                                {Header}
                            </MarkDown>
                        </MotionMaybe>
                        <MotionMaybe
                            enable={animateText && enableAnim}
                            k={`${baseKey}-body`}
                            motionProps={childMotionProps}
                        >
                            <MarkDown
                                className={`mt-6 text-lg font-text leading-8 ${headerColor}`}
                            >
                                {Body}
                            </MarkDown>
                        </MotionMaybe>
                    </div>
                </div>
            )}

            <div className={`relative z-40 mx-auto max-w-7xl px-6`}>
                {/* Pricing Cards */}
                <MotionMaybe
                    enable={animateFeatures && isStagger && enableAnim}
                    k={`${baseKey}-packages-container`}
                    motionProps={containerMotionProps}
                    className={`mx-auto grid max-w-2xl items-stretch gap-8 lg:max-w-none ${numColumns}`}
                >
                    {Packages.slice(0, 9).map((pack, index) => {
                        const card = (
                            <div
                                className={`mx-auto flex h-full w-full flex-col rounded-[min(1.5rem,var(--shape-radius))] ${
                                    boxColor ? `${boxColor}` : 'bg-black/5'
                                } ${boxBorder} ${
                                    boxColor ? boxTextColor : textColor
                                } p-8 lg:p-10 ${
                                    Packages.length === 5 &&
                                    index === Packages.length - 1
                                        ? 'lg:col-span-2'
                                        : Packages.length === 7 &&
                                          index === Packages.length - 1
                                        ? 'md:col-span-2 lg:col-span-3'
                                        : ''
                                }`}
                            >
                                <div className="flex h-full flex-col">
                                    <div>
                                        {pack.Title && (
                                            <div>
                                                <MarkDown
                                                    variant={
                                                        packageHeadingVariant
                                                    }
                                                    className="mb-7 text-center font-display text-2xl leading-7"
                                                >
                                                    {pack.Title}
                                                </MarkDown>
                                            </div>
                                        )}
                                        {pack.Price && (
                                            <div>
                                                <MarkDown
                                                    className={`mb-7 text-center text-5xl font-extrabold ${iconColor}`}
                                                >
                                                    {pack.Price}
                                                </MarkDown>
                                            </div>
                                        )}
                                        {pack.Description && (
                                            <div>
                                                <MarkDown
                                                    className={`mb-4 text-center ${
                                                        boxColor
                                                            ? boxTextColor
                                                            : textColor
                                                    }`}
                                                >
                                                    {pack.Description}
                                                </MarkDown>
                                            </div>
                                        )}
                                    </div>
                                    <div className="mt-6 flex flex-1 flex-col">
                                        {/* Feature List for each Pricing Card */}
                                        {pack.Features &&
                                        pack.Features.length > 0 ? (
                                            <div className="mx-auto flex-1 space-y-1">
                                                {pack.Features.map(
                                                    (feature, idx) => (
                                                        <Feature
                                                            key={idx}
                                                            {...feature}
                                                            iconColor={
                                                                iconColor
                                                            }
                                                        />
                                                    )
                                                )}
                                            </div>
                                        ) : (
                                            <div className="flex-1" />
                                        )}

                                        {(() => {
                                            const { hasAny, element } = getCTA(
                                                {
                                                    CTA: pack.SmallCTA,
                                                    CustomCTALink:
                                                        pack.SmallCTALink,
                                                    CTAOpenInNewTab:
                                                        pack.SmallCTAOpenInNewTab,
                                                    SecondaryButtonText:
                                                        pack.SmallSecondaryButtonText,
                                                    SecondaryButtonLink:
                                                        pack.SmallSecondaryButtonLink,
                                                    SecondaryButtonOpenInNewTab:
                                                        pack.SmallSecondaryButtonOpenInNewTab,
                                                    OutlineButtonText:
                                                        pack.SmallOutlineButtonText,
                                                    OutlineButtonLink:
                                                        pack.SmallOutlineButtonLink,
                                                    OutlineButtonOpenInNewTab:
                                                        pack.SmallOutlineButtonOpenInNewTab,
                                                },
                                                {
                                                    secondaryVariant:
                                                        buttonVariant,
                                                    outlineVariant:
                                                        buttonVariantOutline,
                                                    buttonAlign:
                                                        'justify-center',
                                                    wrapSpacing: 'mt-8',
                                                    size: 'sm',
                                                }
                                            );
                                            return hasAny ? (
                                                <div className="mt-auto">
                                                    {element}
                                                </div>
                                            ) : null;
                                        })()}
                                    </div>
                                </div>
                            </div>
                        );

                        if (animateFeatures && isStagger && enableAnim) {
                            return (
                                <motion.div
                                    key={`pack-${index}-${baseKey}`}
                                    className="h-full"
                                    variants={childMotionProps.variants}
                                    initial={
                                        EditorPreview
                                            ? childMotionProps.initial
                                            : undefined
                                    }
                                    animate={
                                        EditorPreview
                                            ? childMotionProps.animate
                                            : undefined
                                    }
                                    whileInView={
                                        EditorPreview
                                            ? undefined
                                            : childMotionProps.whileInView
                                    }
                                    viewport={childMotionProps.viewport}
                                >
                                    {card}
                                </motion.div>
                            );
                        }

                        return (
                            <MotionMaybe
                                key={`pack-${index}-${baseKey}`}
                                enable={
                                    animateFeatures && !isStagger && enableAnim
                                }
                                k={`${baseKey}-pack-${index}`}
                                className="h-full"
                                motionProps={childMotionProps}
                            >
                                {card}
                            </MotionMaybe>
                        );
                    })}
                </MotionMaybe>
            </div>

            {/* CTA */}
            <div className="flex w-full justify-center">
                {hasMainCTAs && (
                    <MotionMaybe
                        enable={animateCTAs && enableAnim}
                        k={`${baseKey}-ctas`}
                        motionProps={childMotionProps}
                    >
                        {mainCTAs}
                    </MotionMaybe>
                )}
            </div>
        </div>
    );
}
